import { isNonProductionBuild, isSignupLocalFakesBuild } from '@/services/build-environment.mjs';
import { withDevGapFill } from './dev-gap-fill';
import { loadLocalFakeSignupApi, type LocalFakeSignupApiFactory } from './local-fakes-loader';
import { createRealSignupApi, type RealSignupApiOptions } from './real-signup-api';
import type { SignupApi } from './signup-api';

export interface SignupApiSelection extends RealSignupApiOptions {
  /** The raw `?scenario=` value; only the local fake reads it. */
  scenarioParam: string | null;
  /** Whether this is a local fakes dev server. */
  localFakes?: boolean;
  /** Whether calls with no backend yet fall back to the fake (temporary, see dev-gap-fill.ts). */
  devGapFill?: boolean;
  loadLocalFake?: () => LocalFakeSignupApiFactory | null;
}

/**
 * The SignupApi factory (ADR-053). The real implementation is the default and
 * the answer in every built bundle. The local fake is chosen only when the
 * shared rule says this is a local fakes dev server AND the fake loader is
 * actually present; if the alias that supplies it did not apply, the real
 * implementation answers rather than failing. Dev and preview builds keep the
 * real implementation but fill its not-yet-built calls from the fake.
 */
export function createSignupApi({
  scenarioParam,
  localFakes = isSignupLocalFakesBuild(),
  devGapFill = isNonProductionBuild(),
  loadLocalFake = loadLocalFakeSignupApi,
  captureOnboardingSource,
}: SignupApiSelection): SignupApi {
  if (localFakes) {
    const createFake = loadLocalFake();
    if (createFake) return createFake(scenarioParam);
  }
  const real = createRealSignupApi({ captureOnboardingSource });
  if (devGapFill) {
    const createFake = loadLocalFake();
    if (createFake) return withDevGapFill(real, createFake(scenarioParam));
  }
  return real;
}
